import { useState } from 'react'
import { useApi, useApiMutation } from '../hooks/useApi'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { formatRelativeTime } from '../lib/utils'
import type { Customer, FeatureFlag, CustomerConfig } from '../types'

interface ConfigurationData {
  global_feature_flags: FeatureFlag[]
  global_configs: CustomerConfig[]
  smtp2go_templates: any[]
  system_settings: Record<string, any>
}

interface FeatureFlagFormData {
  feature_name: string
  description: string
  default_enabled: boolean
}

interface ConfigFormData {
  config_key: string
  config_value: string
  is_secret: boolean
  description: string
}

export function Configuration() {
  const [activeTab, setActiveTab] = useState<'features' | 'smtp' | 'system' | 'templates'>('features')
  const [showFeatureFlagModal, setShowFeatureFlagModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [featureFlagForm, setFeatureFlagForm] = useState<FeatureFlagFormData>({
    feature_name: '',
    description: '',
    default_enabled: false,
  })
  const [configForm, setConfigForm] = useState<ConfigFormData>({
    config_key: '',
    config_value: '',
    is_secret: false,
    description: '',
  })

  // Fetch configuration data
  const { data: configData, loading, error, refetch } = useApi<ConfigurationData>('/configuration/global')
  const { data: customers } = useApi<Customer[]>('/customers')

  // Mutations
  const { mutate: saveFeatureFlag, loading: savingFlag } = useApiMutation()
  const { mutate: saveConfig, loading: savingConfig } = useApiMutation()
  const { mutate: deleteItem, loading: deleting } = useApiMutation()
  const { mutate: toggleFeatureFlag, loading: toggling } = useApiMutation()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading configuration
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleSaveFeatureFlag = async () => {
    try {
      if (editingItem) {
        await saveFeatureFlag(`/configuration/feature-flags/${editingItem.id}`, featureFlagForm, { method: 'PUT' })
      } else {
        await saveFeatureFlag('/configuration/feature-flags', featureFlagForm)
      }
      setShowFeatureFlagModal(false)
      setEditingItem(null)
      setFeatureFlagForm({ feature_name: '', description: '', default_enabled: false })
      refetch()
    } catch (error) {
      console.error('Failed to save feature flag:', error)
    }
  }

  const handleSaveConfig = async () => {
    try {
      if (editingItem) {
        await saveConfig(`/configuration/configs/${editingItem.id}`, configForm, { method: 'PUT' })
      } else {
        await saveConfig('/configuration/configs', configForm)
      }
      setShowConfigModal(false)
      setEditingItem(null)
      setConfigForm({ config_key: '', config_value: '', is_secret: false, description: '' })
      refetch()
    } catch (error) {
      console.error('Failed to save config:', error)
    }
  }

  const handleToggleFeatureFlag = async (flagId: string, enabled: boolean) => {
    try {
      await toggleFeatureFlag(`/configuration/feature-flags/${flagId}/toggle`, { enabled: !enabled })
      refetch()
    } catch (error) {
      console.error('Failed to toggle feature flag:', error)
    }
  }

  const handleDelete = async (type: string, id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return
    
    try {
      await deleteItem(`/configuration/${type}/${id}`, {}, { method: 'DELETE' })
      refetch()
    } catch (error) {
      console.error('Failed to delete item:', error)
    }
  }

  const openFeatureFlagModal = (flag?: any) => {
    if (flag) {
      setEditingItem(flag)
      setFeatureFlagForm({
        feature_name: flag.feature_name,
        description: flag.description || '',
        default_enabled: flag.enabled,
      })
    } else {
      setEditingItem(null)
      setFeatureFlagForm({ feature_name: '', description: '', default_enabled: false })
    }
    setShowFeatureFlagModal(true)
  }

  const openConfigModal = (config?: any) => {
    if (config) {
      setEditingItem(config)
      setConfigForm({
        config_key: config.config_key,
        config_value: config.is_secret ? '' : config.config_value || '',
        is_secret: config.is_secret,
        description: config.description || '',
      })
    } else {
      setEditingItem(null)
      setConfigForm({ config_key: '', config_value: '', is_secret: false, description: '' })
    }
    setShowConfigModal(true)
  }

  const tabs = [
    { id: 'features', label: 'Feature Flags', icon: '🚩' },
    { id: 'smtp', label: 'SMTP2GO Config', icon: '📧' },
    { id: 'system', label: 'System Settings', icon: '⚙️' },
    { id: 'templates', label: 'Templates', icon: '📄' },
  ] as const

  const renderFeatureFlagsTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Global Feature Flags</CardTitle>
            <Button onClick={() => openFeatureFlagModal()}>
              Add Feature Flag
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {configData?.global_feature_flags && configData.global_feature_flags.length > 0 ? (
            <div className="space-y-4">
              {configData.global_feature_flags.map((flag) => (
                <div key={flag.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-sm font-medium text-gray-900">
                        {flag.feature_name}
                      </h4>
                      <Badge variant={flag.enabled ? 'success' : 'default'}>
                        {flag.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Updated {formatRelativeTime(flag.updated_at)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFeatureFlag(flag.id, flag.enabled)}
                      loading={toggling}
                    >
                      Toggle
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openFeatureFlagModal(flag)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete('feature-flags', flag.id)}
                      loading={deleting}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🚩</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No feature flags configured
              </h3>
              <p className="text-gray-500 mb-6">
                Create feature flags to control functionality across all customers.
              </p>
              <Button onClick={() => openFeatureFlagModal()}>
                Create First Feature Flag
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Feature Flag Override */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Overrides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 mb-4">
            View and manage per-customer feature flag overrides. Global settings apply unless overridden.
          </div>
          
          {customers && customers.length > 0 ? (
            <div className="space-y-3">
              {customers.slice(0, 5).map((customer) => (
                <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {customer.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {customer.domain || 'No domain'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="info">
                      {configData?.global_feature_flags?.filter(f => f.enabled).length || 0} enabled
                    </Badge>
                    <Button variant="outline" size="sm">
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
              {customers.length > 5 && (
                <div className="text-center pt-2">
                  <Button variant="ghost" size="sm">
                    View All Customers ({customers.length - 5} more)
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">No customers found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )

  const renderSMTPTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>SMTP2GO Configuration</CardTitle>
            <Button onClick={() => openConfigModal()}>
              Add Configuration
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Default SMTP2GO Settings */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Default SMTP2GO Settings
              </h4>
              <div className="text-sm text-blue-700">
                These settings are used as defaults for new customer deployments.
              </div>
            </div>

            {configData?.global_configs?.filter(c => c.config_key.includes('smtp2go')).map((config) => (
              <div key={config.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      {config.config_key}
                    </h4>
                    {config.is_secret && (
                      <Badge variant="warning" className="text-xs">Secret</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {config.is_secret ? '••••••••' : config.config_value || 'Not set'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Updated {formatRelativeTime(config.updated_at)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openConfigModal(config)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete('configs', config.id)}
                    loading={deleting}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )) || (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 mb-4">No SMTP2GO configuration found</p>
                <Button onClick={() => openConfigModal()}>
                  Add SMTP2GO Configuration
                </Button>
              </div>
            )}

            {/* Template Configuration */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Email Templates
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">Welcome Email</div>
                  <div className="text-xs text-gray-500 mt-1">Template ID: welcome-001</div>
                  <div className="mt-2">
                    <Button variant="outline" size="sm">Configure</Button>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">Password Reset</div>
                  <div className="text-xs text-gray-500 mt-1">Template ID: reset-001</div>
                  <div className="mt-2">
                    <Button variant="outline" size="sm">Configure</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderSystemTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>System Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Deployment Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900">Deployment Settings</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Default Region</div>
                      <div className="text-xs text-gray-500">UK</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Batch Size</div>
                      <div className="text-xs text-gray-500">5 customers</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Failure Threshold</div>
                      <div className="text-xs text-gray-500">20%</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                </div>
              </div>

              {/* Monitoring Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900">Monitoring Settings</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Health Check Interval</div>
                      <div className="text-xs text-gray-500">5 minutes</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Alert Cooldown</div>
                      <div className="text-xs text-gray-500">15 minutes</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Retention Period</div>
                      <div className="text-xs text-gray-500">90 days</div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Security Settings</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Session Timeout</div>
                    <div className="text-xs text-gray-500">8 hours</div>
                  </div>
                  <Button variant="ghost" size="sm">Edit</Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">MFA Required</div>
                    <div className="text-xs text-gray-500">Enabled</div>
                  </div>
                  <Badge variant="success">Enabled</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Audit Log Retention</div>
                    <div className="text-xs text-gray-500">7 years</div>
                  </div>
                  <Button variant="ghost" size="sm">Edit</Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderTemplatesTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Configuration Templates</CardTitle>
            <Button>Create Template</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 mb-6">
            Templates provide pre-configured settings for different customer types or use cases.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Standard Customer</h4>
                <Badge variant="success">Active</Badge>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Default configuration for standard customers with basic features.
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Region:</span>
                  <span className="text-gray-900">UK</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Features:</span>
                  <span className="text-gray-900">5 enabled</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">SMTP:</span>
                  <span className="text-gray-900">Shared</span>
                </div>
              </div>
              <div className="mt-4 flex space-x-2">
                <Button variant="outline" size="sm">Edit</Button>
                <Button variant="ghost" size="sm">Clone</Button>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Enterprise Customer</h4>
                <Badge variant="info">Active</Badge>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Enhanced configuration for enterprise customers with premium features.
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Region:</span>
                  <span className="text-gray-900">EU</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Features:</span>
                  <span className="text-gray-900">12 enabled</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">SMTP:</span>
                  <span className="text-gray-900">Dedicated</span>
                </div>
              </div>
              <div className="mt-4 flex space-x-2">
                <Button variant="outline" size="sm">Edit</Button>
                <Button variant="ghost" size="sm">Clone</Button>
              </div>
            </div>

            <div className="border border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 text-2xl mb-2">+</div>
                <div className="text-sm text-gray-500 mb-2">Create New Template</div>
                <Button variant="outline" size="sm">Add Template</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage global settings, feature flags, and configuration templates
            </p>
          </div>
          <Button variant="outline">
            Export Configuration
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'features' && renderFeatureFlagsTab()}
      {activeTab === 'smtp' && renderSMTPTab()}
      {activeTab === 'system' && renderSystemTab()}
      {activeTab === 'templates' && renderTemplatesTab()}

      {/* Feature Flag Modal */}
      <Modal
        isOpen={showFeatureFlagModal}
        onClose={() => setShowFeatureFlagModal(false)}
        title={editingItem ? 'Edit Feature Flag' : 'Add Feature Flag'}
      >
        <div className="space-y-4">
          <Input
            label="Feature Name"
            value={featureFlagForm.feature_name}
            onChange={(e) => setFeatureFlagForm(prev => ({ ...prev, feature_name: e.target.value }))}
            placeholder="feature_name"
            helperText="Use snake_case format"
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={featureFlagForm.description}
              onChange={(e) => setFeatureFlagForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Describe what this feature flag controls..."
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="default_enabled"
              checked={featureFlagForm.default_enabled}
              onChange={(e) => setFeatureFlagForm(prev => ({ ...prev, default_enabled: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="default_enabled" className="ml-2 block text-sm text-gray-900">
              Enabled by default for new customers
            </label>
          </div>
          
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button variant="ghost" onClick={() => setShowFeatureFlagModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFeatureFlag} loading={savingFlag}>
              {editingItem ? 'Update' : 'Create'} Feature Flag
            </Button>
          </div>
        </div>
      </Modal>

      {/* Config Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title={editingItem ? 'Edit Configuration' : 'Add Configuration'}
      >
        <div className="space-y-4">
          <Input
            label="Configuration Key"
            value={configForm.config_key}
            onChange={(e) => setConfigForm(prev => ({ ...prev, config_key: e.target.value }))}
            placeholder="smtp2go_api_key"
            helperText="Use snake_case format"
          />
          
          <Input
            label="Configuration Value"
            type={configForm.is_secret ? "password" : "text"}
            value={configForm.config_value}
            onChange={(e) => setConfigForm(prev => ({ ...prev, config_value: e.target.value }))}
            placeholder="Enter configuration value"
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={configForm.description}
              onChange={(e) => setConfigForm(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Describe this configuration..."
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_secret"
              checked={configForm.is_secret}
              onChange={(e) => setConfigForm(prev => ({ ...prev, is_secret: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_secret" className="ml-2 block text-sm text-gray-900">
              This is a secret value (will be stored encrypted)
            </label>
          </div>
          
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button variant="ghost" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} loading={savingConfig}>
              {editingItem ? 'Update' : 'Create'} Configuration
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}